import React from 'react';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useCallback } from 'react';
import { CreateEventHeaderLayoutSettings, CreateEventPayload0301, WebsiteDesignSettingsInput } from '@graphql/generated';
import { useAuth } from '@shared/components/AuthProvider';
import { getNodeEnv } from '@shared/utils/getEnvironmentVariables';
import { useToast } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { WarningTriangle } from '@withjoy/joykit/icons';
import { ValidationError } from 'yup';

export type DefaultTemplatePayload = Readonly<{
  template: string;
  theme: string;
  websiteDesignSettings: Readonly<WebsiteDesignSettingsInput>;
  headerLayout: Readonly<CreateEventHeaderLayoutSettings>;
}>;

const { isDevelopment } = getNodeEnv();

const formatValidationErrors = (error: unknown): string => {
  if (error instanceof ValidationError && error.errors?.length > 0) {
    return error.errors?.map((err, index) => `${index + 1}. ${err}`).join('\n');
  }

  if (error instanceof Error) {
    return `1. ${error.message}`;
  }

  return '1. Unknown validation error';
};

export const useDefaultTemplatePayload = () => {
  const { payload: defaultTemplatePayload } = useFeatureValue('createWeddingDefaultThemeExperiment');
  const { isSuperAdmin } = useAuth();
  const { toast } = useToast();
  const { tq } = useTranslation('createWedding');

  const applyDefaultTemplatePayload = useCallback(
    async (payload: CreateEventPayload0301) => {
      if (defaultTemplatePayload && (isSuperAdmin || isDevelopment)) {
        //All schema go through testing in development environment. The validation is not added to production due to performance reasons.
        const { websiteDesignSchema } = await import('./util');

        try {
          websiteDesignSchema.validateSync(defaultTemplatePayload, { strict: true, abortEarly: false });
        } catch (error) {
          toast(tq`Invalid template settings payload`, {
            icon: <WarningTriangle />,
            position: 'top-center',
          });
          const formattedErrors = formatValidationErrors(error);
          console.error('Validation errors:\n' + formattedErrors);
          return { payload, error: true };
        }
      }

      if (defaultTemplatePayload) {
        const validatedDefaultTemplatePayload = defaultTemplatePayload as DefaultTemplatePayload;
        return {
          payload: {
            ...payload,
            template: validatedDefaultTemplatePayload.template,
            theme: validatedDefaultTemplatePayload.theme,
            websiteDesignSettings: validatedDefaultTemplatePayload.websiteDesignSettings,
            headerLayout: validatedDefaultTemplatePayload.headerLayout,
          },
          error: false,
        };
      }
      return { payload, error: false };
    },
    [defaultTemplatePayload, isSuperAdmin, tq, toast],
  );

  return applyDefaultTemplatePayload;
};
